import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IPinpointTrigger, PinpointEmailBaseCampaign, Segment } from '@feathr/blackbox';
import { CampaignState, transformTriggersToPredicates } from '@feathr/blackbox';
import {
  AlertV2 as Alert,
  CardHeader,
  CardV2 as Card,
  EAlertV2Type as AlertType,
  Form,
} from '@feathr/components';
import ConsentCapture from '@feathr/extender/components/ConsentCapture';
import { useStore, useUser } from '@feathr/extender/state';
import { moment, TimeFormat } from '@feathr/hooks';
import type { TValidateGrouped } from '@feathr/rachis';

import EmailCampaignSendWindow from '../../EmailCampaignSendWindow';
import PinpointEmailCampaignAudienceSummary from '../../PinpointEmailCampaignAudienceSummary';

interface IProps {
  campaign: PinpointEmailBaseCampaign;
  disabled: boolean;
  setCurrentStep: (index: number) => void;
}

interface IErrors extends TValidateGrouped {
  date_send_start?: string[];
  date_send_end?: string[];
  'consent.has_consent'?: string[];
}

export function validateStepScheduleSend(campaign: PinpointEmailBaseCampaign): IErrors {
  return campaign.validate(
    ['date_send_start', 'date_send_end', 'consent.has_consent'],
    false,
    'grouped',
  ).errors;
}

function AutoPinpointEmailCampaignStepScheduleSend({
  campaign,
  disabled,
  setCurrentStep,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Segments } = useStore();

  const user = useUser();
  const stats = campaign.get('total_stats');

  const timeToSend = campaign.get('time_to_send');
  const bulkSend = campaign.get('send_all');
  const triggers = campaign.get('actions');
  const isTimeSubtype = campaign.get('subtype') === 'time';

  function handleChangeConsent(newValue = false): void {
    if (newValue) {
      campaign.set({
        consent: {
          user: user!.id,
          has_consent: true,
          date_consented: moment.utc().format(TimeFormat.isoDateTime),
        },
      });
    } else {
      campaign.set({
        consent: {
          has_consent: false,
        },
      });
    }
  }

  const validationErrors = validateStepScheduleSend(campaign);

  // Ephemeral segment to get the count of people who will receive the email.
  const [segment] = useState<Segment>(() =>
    Segments.create({
      account: campaign.get('account'),
      is_conversion_segment: false,
      is_custom: false,
      lookback_mode: 'unbounded',
      mode: 'match_all',
      predicates: [
        {
          kind: 'attribute',
          attr_against: 'email',
          attr_type: 'string',
          comparison: 'exists',
        },
        {
          group: transformTriggersToPredicates(campaign.get('actions')),
          group_mode: campaign.get('mode') ?? 'match_any',
          kind: triggers.some((trigger: IPinpointTrigger) => trigger.kind === 'update')
            ? 'attribute'
            : 'activity',
        },
      ],
    }),
  );

  const peopleCount = segment.count;

  const handleSetStep = useCallback(
    (index: number) => {
      return function (): void {
        setCurrentStep(index);
      };
    },
    [setCurrentStep],
  );

  return (
    <Form
      description={t(
        'Choose or create a group of people that will receive this email. Anyone in this group will receive your email at the target send time, set below.',
      )}
      label={t('Edit Campaign: Schedule')}
      width={'wide'}
    >
      <Card width={'full'}>
        <CardHeader
          description={t(
            'People who meet the triggers of this campaign during the specified timeframe will receive your message. The maximum time window is one year.',
          )}
          title={t('Send Window')}
        />
        <Card.Content addVerticalGap={true}>
          <EmailCampaignSendWindow
            campaign={campaign}
            isDisabled={disabled}
            isTimeSubtype={isTimeSubtype}
            timeToSend={timeToSend}
            validationErrors={validationErrors}
          />
        </Card.Content>
      </Card>
      <Card width={'full'}>
        <Card.Header title={t('Verify & Publish')} />
        <Card.Content>
          {bulkSend && peopleCount > 0 && (
            <Alert
              description={t(
                'A separate single send campaign will also be sent alongside this Auto Send Campaign to {{ count, number }} person who has previously met your specified criteria.',
                { count: peopleCount },
              )}
              title={t('Single Send Campaign created')}
              type={AlertType.warning}
            >
              <button name={'change_bulk_send'} onClick={handleSetStep(2)}>
                {t('Change this setting')}
              </button>
            </Alert>
          )}
          <ConsentCapture
            alertDescription={t(
              'I understand that if my campaigns exhibit abnormally high bounce or complaint rates, it may result in my account being placed under temporary review or suspension.',
            )}
            alertTitle={t(
              'I attest that I am sending this email to recipients who have signed up and specifically requested to receive email about this topic from me or my organization.',
            )}
            disabled={disabled}
            label={t(
              'Please type "AGREE" in the box below to verify the above statement (case-sensitive).',
            )}
            onChange={handleChangeConsent}
            value={campaign.get('consent', { has_consent: false }).has_consent}
          />
        </Card.Content>
      </Card>
      {!!stats.num_targeted &&
        [CampaignState.Published, CampaignState.Stopped].includes(campaign.get('state')) &&
        campaign.isAfterDateSendStart() && <PinpointEmailCampaignAudienceSummary stats={stats} />}
    </Form>
  );
}

export default observer(AutoPinpointEmailCampaignStepScheduleSend);
